import { Point2D } from "../common/geometry";
import {
  MODE_SIT,
  MODE_STAIR,
  MODE_CRAWL,
  MODE_STAND,
  MODE_STANDARD,
  MODE_WALK,
} from "../consts";

export const configMode = (m: string) => ["/config/mode", m] as const;
export const sit = () => configMode(MODE_SIT);
export const stand = () => configMode(MODE_STAND);
export const walk = () => configMode(MODE_WALK);
export const crawl = () => configMode(MODE_CRAWL);
export const stair = () => configMode(MODE_STAIR);
export const standard = () => configMode(MODE_STANDARD);

// config height
export const configHeight = (height: number) =>
  ["/config/height", height] as const;

// config aviidance
export const configAvoidance = (m: number) => ["/config/avoidance", m] as const;

//config arm torque
export const configArmTorque = (torque: number) =>
  ["/arm/config/gripper_torque", torque] as const;

//config arm torque
export const configGripDepth = (depth: number) =>
  ["/arm/config/gripper_depth", depth] as const;

// config light
export const configLight = (on: boolean) => ["/config/light", on] as const;
export const configBrightness = (brightness: number) =>
  ["/config/brightness", brightness] as const;

// config max_speed
export const configMaxSpeed = (speed: number) =>
  ["/config/max_speed", speed] as const;

// config motor
export const MOTOR_POWER_OFF = "POWER_OFF";
export const MOTOR_POWER_ON = "POWER_ON";

export const configMotor = (action: string) =>
  ["/config/motor", action] as const;
export const motorOn = () => configMotor(MOTOR_POWER_ON);
export const motorOff = () => configMotor(MOTOR_POWER_OFF);
export const motorOnOff = (on: boolean) => (on ? motorOn() : motorOff());

// config estop
export const configEstop = (activate: string) =>
  ["/config/estop", activate] as const;

// claim motor
export const claimMotor = () => ["/claim/motor", true] as const;
export const releaseMotor = () => ["/claim/motor", false] as const;

// claim estop
export const claimEstop = () => ["/claim/estop", true] as const;
export const releaseEstop = () => ["/claim/estop", false] as const;

// perform actions
export const selfRight = () => ["/perform/selfright"] as const;
export const turnOver = () => ["/perform/turnover"] as const;

export type MoveOptions = {
  x: number;
  y: number;
  z: number;
};

export const move = ({ x, y, z }: MoveOptions) =>
  [
    "/perform/movement",
    {
      linear: {
        x: x,
        y: y,
        z: 0,
      },
      angular: {
        x: 0,
        y: 0,
        z: z,
      },
    },
  ] as const;
export const resetPose = () => ["/perform/reset"] as const;

export const stow = () => ["/arm/perform/stow"] as const;
export const unstow = () => ["/arm/perform/unstow"] as const;
export const carry = () => ["/arm/perform/carry"] as const;
export const autoGrip = (position: Point2D) =>
  ["/arm/perform/grasp", position] as const;
export const armStop = () => ["/arm/perform/stop"] as const;
export const joyCtrl = (
  left_x: number,
  left_y: number,
  right_x: number,
  right_y: number,
  left_trigger: number,
  right_trigger: number
) =>
  [
    "/arm/perform/joy_ctrl",
    { left_x, left_y, right_x, right_y, left_trigger, right_trigger },
  ] as const;
export const joyValveCtrl = (
  left_x: number,
  left_y: number,
  right_x: number,
  right_y: number,
  left_trigger: number,
  right_trigger: number
) =>
  [
    "/arm/perform/joy_valve_ctrl",
    { left_x, left_y, right_x, right_y, left_trigger, right_trigger },
  ] as const;
export const gripperClose = () => ["/arm/perform/gripper_close"] as const;
export const gripperOpen = () => ["/arm/perform/gripper_open"] as const;
export const pickObject = () =>
  ["/arm/perform/pickup_object_in_image"] as const;
export const configArm = (max_speed: number) =>
  ["/arm/config/arm_velocity", max_speed] as const;
export const configWrist = (max_speed: number) =>
  ["/arm/config/wrist_velocity", max_speed] as const;
export const configVelocityDuration = (sec: number) =>
  ["/arm/config/gripper_torque", sec] as const;
export const configGripper = (torque: number) =>
  ["/arm/config/gripper_torque", torque] as const;
// mission
export const missionRecordStart = () => ["/mission/record", "START"] as const;
export const missionRecordStop = () => ["/mission/record", "STOP"] as const;
export const missionRecordAddWayPoint = () =>
  ["/mission/record", "ADD_POINT"] as const;
export const missionRunStart = (id: string, map_id: string) =>
  ["/mission/start", { mission_id: id, map_id: map_id }] as const;
export const missionRunPause = () => [
  "/mission/pause/config",
  "PAUSE",
]; /* Changed "/mission/pause/config", "RE_START" */
export const missionRunStop = () => [
  "/mission/stop",
  "",
]; /* Changed "/mission/stop", "" */
export const missionClear = () => ["/mission/clear"] as const;
export const missionRename = (from: string, to: string) =>
  ["/mission/rename", { from_name: from, to_name: to }] as const;
export const missionDelete = (id: string) =>
  ["/mission/delete", { mission_id: id }] as const;
export const missionList = () => ["/mission/list"] as const;
export const missionMap = (id: string) =>
  ["/mission/map", { mission_id: id }] as const;
export const missionRunRestart = () => [
  "/mission/pause/config",
  "RE_START",
]; /* Added missionRunRestart */

// custom action

export const missionRecordCustomStart = () =>
  ["/mission/record/custom_movement/start", {}] as const;
export const missionRecordCustomEnd = () =>
  ["/mission/record/custom_movement/end", {}] as const;

export const missionAddCustomAction = (action: string, data: any) =>
  ["/mission/record", "ADD_POINT"] as const;
export const CUSTOM_ACTION_TEST_CMD = "TEST_CMD";
export const CUSTOM_ACTION_DETECT_ART_DMG = "DETECT_ART_DMG";

export const CUSTOM_ACTION_PTZ_INSPECTION = "PTZ_INSPECTION";

export const CUSTOM_ACTION_PREPARE_ART_REF = "PREPARE_ART_REF";
export const missionDetectArtDmg = (art_id: string) =>
  missionAddCustomAction(CUSTOM_ACTION_DETECT_ART_DMG, { art_id });
export type PTZMovement = {
  pan: number;
  tilt: number;
  zoom?: number;
};
export const missionLockInspection = (ptzMovement: PTZMovement) =>
  missionAddCustomAction(CUSTOM_ACTION_PTZ_INSPECTION, ptzMovement);
export const missionTestCmd = () =>
  missionAddCustomAction(CUSTOM_ACTION_TEST_CMD, null);
export const missionPrepareArtRef = () =>
  ["/art_inspection", { cmd: "/prepare_ref", data: "" }] as const;
export const missionListArtRef = () =>
  ["/art_inspection", { cmd: "/list_art", data: "" }] as const;
export const missionDeleteArtRef = (art_id: string) =>
  ["/art_inspection", { cmd: "/delete_ref", data: art_id }] as const;

export const handleKeyDown = (keyboardCode: string) => {
  switch (keyboardCode) {
    case "KeyW":
    case "ArrowUp":
      // Handle "forward"
      return { axis: "x", value: -1 };
    case "KeyS":
    case "ArrowDown":
      // Handle "back"
      return { axis: "x", value: 1 };
    case "KeyA":
    case "ArrowLeft":
      // Handle "turn left"
      return { axis: "y", value: -1 };
    case "KeyD":
    case "ArrowRight":
      // Handle "turn right"
      return { axis: "y", value: 1 };
    case "KeyQ":
      return { axis: "z", value: -1 };
    case "KeyE":
      return { axis: "z", value: 1 };
    default:
      return {};
  }
};

export const handleKeyUp = (keyboardCode: string) => {
  switch (keyboardCode) {
    case "KeyS":
    case "ArrowDown":
    case "KeyW":
    case "ArrowUp":
      // Handle "back"
      return { axis: "x", value: 0 };
    case "KeyA":
    case "ArrowLeft":
    case "KeyD":
    case "ArrowRight":
      // Handle "turn left"
      return { axis: "y", value: 0 };
    case "KeyE":
    case "KeyQ":
      return { axis: "z", value: 0 };
    default:
      return {};
  }
};

export const ptzCtrl = ({ pan, tilt, zoom }: PTZMovement) =>
  ["/ptz/perform/movement", JSON.stringify({ pan, tilt, zoom })] as const;
export const inspectPTZImage = ({ pan, tilt, zoom }: PTZMovement) =>
  [
    "/ptz_inspection",
    {
      init_theta: pan,
      init_phi: tilt,
      init_zoom: zoom,
    },
  ] as const;
export const getPTZFrame = () => ["/ptz/get_frame"] as const;
